import React, { memo, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Button, Typography } from '@mui/material';
import { ButtonGroupWrapper } from '@sharedCR/coreDesignSystem/Buttons';
import { TextFieldCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import { COMMON_REQUEST_DATETIME_FORMAT } from 'constants/date';
import useForm from 'hooks/useForm';

import { closeDialogAction } from 'store/global/actions';

import {
  bulkPasteButtonsStyles,
  bulkPasteTextAreaStyles,
  bulkPasteWrapperStyles,
} from './PreInvestorConditions.styles';
import { PreInvestorConditionsTestIds } from './PreInvestorConditions.testIds';

const INITIAL_FORM = { conditionText: '' };

const PreInvestorBulkPasteModal = ({ dialogId, onAddConditions }) => {
  const dispatch = useDispatch();

  const { form, handleChange } = useForm(INITIAL_FORM, {}, []);

  const onClose = useCallback(() => {
    dispatch(closeDialogAction(dialogId));
  }, [dispatch, dialogId]);

  const onPaste = useCallback(() => {
    const lines = form.conditionText
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length);

    const newConditions = lines.map(line => ({
      id: crypto.randomUUID(),
      conditionText: line,
      dateAdded: dayjs().format(COMMON_REQUEST_DATETIME_FORMAT),
      dateResponded: null,
      dateCleared: null,
    }));

    onAddConditions(newConditions);
    onClose();
  }, [form.conditionText, onAddConditions, onClose]);

  return (
    <Box sx={bulkPasteWrapperStyles}>
      <Typography variant="regular14">
        Paste conditions below, one per line. Each line will be added as a separate condition with
        today&apos;s date.
      </Typography>
      <TextFieldCDS
        multiline
        dataTestId={PreInvestorConditionsTestIds.BULK_PASTE_INPUT}
        name="conditionText"
        placeholder="Paste conditions here, one per line..."
        rows={8}
        sx={bulkPasteTextAreaStyles}
        value={form.conditionText}
        onChange={handleChange}
      />
      <ButtonGroupWrapper sx={bulkPasteButtonsStyles}>
        <Button
          data-testid={PreInvestorConditionsTestIds.BULK_PASTE_CANCEL_BUTTON}
          size="large"
          variant="line"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          data-testid={PreInvestorConditionsTestIds.BULK_PASTE_ADD_BUTTON}
          disabled={!form.conditionText.trim().length}
          size="large"
          onClick={onPaste}
        >
          Add Conditions
        </Button>
      </ButtonGroupWrapper>
    </Box>
  );
};

PreInvestorBulkPasteModal.propTypes = {
  dialogId: PropTypes.string.isRequired,
  onAddConditions: PropTypes.func.isRequired,
};

export default memo(PreInvestorBulkPasteModal);
